import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type ChipTone = 'neutral' | 'brand' | 'accent' | 'amber' | 'grass' | 'quiet';

/**
 * The one small-label element in the application.
 *
 * Before this, statuses and counters were bare uppercase text floating in a
 * table cell, which reads as a stray word rather than as a value. A chip is a
 * container, so the eye groups it as one thing and can skip it.
 *
 * Tone is meaning, not decoration, and it stays inside the rationing rule:
 * `accent` (red) is only ever "live on the site" or destructive, `amber` is a
 * soft warning, `grass` appears on a settled positive state. Everything else
 * is neutral — most chips should be.
 */
const TONES: Record<ChipTone, string> = {
  neutral: 'border-line bg-raised text-muted',
  brand: 'border-brand-500/25 bg-brand-500/10 text-brand-600 dark:text-brand-300',
  accent: 'border-accent-500/25 bg-accent-500/10 text-accent-600 dark:text-accent-300',
  amber: 'border-amber-400/30 bg-amber-400/10 text-amber-600 dark:text-amber-400',
  grass: 'border-grass-400/30 bg-grass-400/10 text-grass-500 dark:text-grass-400',
  quiet: 'border-transparent bg-transparent text-faint',
};

export function Chip({
  children,
  tone = 'neutral',
  icon,
  mono,
  className,
}: {
  children: ReactNode;
  tone?: ChipTone;
  icon?: ReactNode;
  mono?: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-chip border px-2 py-0.5',
        'text-[0.6875rem] font-medium leading-5',
        mono && 'font-mono tabular-nums',
        TONES[tone],
        className,
      )}
    >
      {icon}
      {children}
    </span>
  );
}
